import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Send, MessageSquarePlus, X, Loader2 } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { useAuth } from '@/lib/auth-store';
import { RealtimeChannel } from '@/lib/realtime';
import {
  conversationsCache,
  useConversationsQuery,
  useMarkConversationReadMutation,
  useMessagesQuery,
  useSendMessageMutation,
} from '@/hooks/queries/useConversations';
import { api, ApiError, type ConversationSummaryShape } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Two-pane inbox.
 *   Left:  list of conversations (sorted by last_message_at, unread bolded)
 *   Right: open conversation — message list + composer + live WS subscription.
 *
 * Phase 4b scope: 1-on-1 DMs work end-to-end; group/course-room kinds render
 * but the UI to *create* a non-direct conversation lands in 4c.
 */
export function Inbox() {
  const { state } = useAuth();
  if (state.status !== 'authenticated') return null;

  const { conversationId } = useParams<{ conversationId?: string }>();

  // Mobile layout rule: phones are too narrow to show both panes side
  // by side. We treat the URL as the source of truth — when a
  // conversation is selected (`/messages/:id`), show only the thread
  // pane on mobile; otherwise show only the conversation list. On
  // `md:` and up, the original two-pane layout takes over.
  const showList = !conversationId;
  const showThread = !!conversationId;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader />
      <main className="container flex flex-1 flex-col gap-4 py-6 md:flex-row">
        <div className={cn(showList ? 'flex' : 'hidden', 'md:flex')}>
          <ConversationsPane token={state.token} activeConversationId={conversationId ?? null} />
        </div>
        <div
          className={cn(
            'flex-1 rounded-2xl border border-border bg-card',
            showThread ? 'flex flex-col' : 'hidden md:block',
          )}
        >
          {conversationId ? (
            <ConversationView
              key={conversationId}
              conversationId={conversationId}
              token={state.token}
              myProfileId={state.profile.id}
            />
          ) : (
            <EmptyPane />
          )}
        </div>
      </main>
    </div>
  );
}

function ConversationsPane({
  token,
  activeConversationId,
}: {
  token: string;
  activeConversationId: string | null;
}) {
  const { data, isLoading } = useConversationsQuery(token);
  const conversations = data?.conversations ?? [];
  const [composing, setComposing] = useState(false);

  return (
    <aside className="flex w-full shrink-0 flex-col rounded-2xl border border-border bg-card md:w-80">
      <div className="flex items-center justify-between border-b border-border p-4">
        <h2 className="font-display text-lg font-semibold">Inbox</h2>
        <button
          type="button"
          aria-label="New conversation"
          onClick={() => setComposing(true)}
          className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          title="Start a direct message by email"
        >
          <MessageSquarePlus className="h-4 w-4" />
        </button>
      </div>

      {composing && <NewConversationDialog token={token} onClose={() => setComposing(false)} />}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading…</div>
        ) : conversations.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No conversations yet.</div>
        ) : (
          <ul className="divide-y divide-border">
            {conversations.map((c) => (
              <ConversationRow key={c.id} conversation={c} active={c.id === activeConversationId} />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function ConversationRow({
  conversation: c,
  active,
}: {
  conversation: ConversationSummaryShape;
  active: boolean;
}) {
  const display = c.title ?? '(untitled conversation)';
  return (
    <li>
      <Link
        to={`/messages/${c.id}`}
        className={cn(
          'block px-4 py-3 text-sm transition-colors hover:bg-secondary/50',
          active && 'bg-secondary',
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className={cn('truncate', c.unread && 'font-semibold')}>{display}</span>
          {c.unread && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="unread" />
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleString() : 'No messages yet'}
        </div>
      </Link>
    </li>
  );
}

function EmptyPane() {
  return (
    <div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">
      Select a conversation on the left.
    </div>
  );
}

function ConversationView({
  conversationId,
  token,
  myProfileId,
}: {
  conversationId: string;
  token: string;
  myProfileId: string;
}) {
  const { data, isLoading } = useMessagesQuery(token, conversationId);
  const queryClient = useQueryClient();
  const sendMutation = useSendMessageMutation(token, conversationId);
  const markReadMutation = useMarkConversationReadMutation(token, conversationId);
  const [draft, setDraft] = useState('');

  // Open WS to this conversation, mutating the cache on incoming messages.
  // The realtime channel itself owns reconnection.
  useEffect(() => {
    const channel = new RealtimeChannel(`/ws/conversations/${conversationId}`, () => token);
    const unsub = channel.subscribe((event) => {
      if (
        event &&
        typeof event === 'object' &&
        (event as { event?: unknown }).event === 'message'
      ) {
        const message = (event as { message: import('@/lib/api').StoredMessageShape }).message;
        conversationsCache.appendMessage(queryClient, conversationId, message);
      }
    });
    void channel.connect();
    return () => {
      unsub();
      channel.close();
    };
  }, [conversationId, queryClient, token]);

  // Mark read on open (and on cleanup when the user leaves the thread).
  useEffect(() => {
    void markReadMutation.mutateAsync().catch(() => {
      /* ignore — best-effort */
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const messages = useMemo(() => data?.messages ?? [], [data]);
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [messages.length]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        {/* Mobile-only back link — on phones the inbox list is hidden
            when a thread is open, so this is the only way back without
            using browser-back. */}
        <Link
          to="/messages"
          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground md:hidden"
          aria-label="Back to inbox"
        >
          ← Inbox
        </Link>
        <h2 className="font-display text-base font-semibold">Conversation</h2>
      </div>
      <div ref={scrollerRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading messages…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">No messages yet. Start the thread below.</p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={cn(
                'max-w-[75%] rounded-2xl px-3 py-2 text-sm',
                m.senderProfileId === myProfileId
                  ? 'ml-auto bg-primary text-primary-foreground'
                  : 'bg-secondary text-foreground',
              )}
            >
              <div className="whitespace-pre-wrap break-words">{m.body}</div>
              <div
                className={cn(
                  'mt-1 text-[10px]',
                  m.senderProfileId === myProfileId
                    ? 'text-primary-foreground/70'
                    : 'text-muted-foreground',
                )}
              >
                {new Date(m.createdAt).toLocaleTimeString()}
              </div>
            </div>
          ))
        )}
      </div>
      <form
        className="flex items-center gap-2 border-t border-border p-3"
        onSubmit={async (e) => {
          e.preventDefault();
          const body = draft.trim();
          if (!body || sendMutation.isPending) return;
          setDraft('');
          try {
            await sendMutation.mutateAsync({ body });
          } catch (err) {
            console.error('[inbox] send failed', err);
            setDraft(body); // restore so the user can retry
          }
        }}
      >
        <input
          type="text"
          autoComplete="off"
          aria-label="Message"
          placeholder="Write a message…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <button
          type="submit"
          disabled={!draft.trim() || sendMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          Send
        </button>
      </form>
    </div>
  );
}

/**
 * Tiny modal for starting a 1-on-1 DM by email. Idempotent — if a direct
 * conversation already exists with that recipient, the API returns the
 * existing id and the modal navigates to it.
 */
function NewConversationDialog({ token, onClose }: { token: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  const startMutation = useMutation({
    mutationFn: () => api.conversations.startWithEmail(token, { email: email.trim() }),
    onSuccess: ({ id }) => {
      onClose();
      navigate(`/messages/${id}`);
    },
  });

  const errorMessage =
    startMutation.error instanceof ApiError
      ? startMutation.error.code === 'recipient_not_found'
        ? 'No EduBorg account uses that email yet.'
        : startMutation.error.code === 'cannot_message_self'
          ? "You can't DM yourself."
          : "Couldn't start that conversation."
      : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm">
      <form
        className="w-full max-w-sm rounded-2xl border border-border bg-background shadow-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          if (!email.trim() || startMutation.isPending) return;
          void startMutation.mutateAsync();
        }}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="font-display text-base font-semibold">New direct message</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 p-5">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Recipient email</span>
            <input
              autoFocus
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="someone@example.com"
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              They need an existing EduBorg account. If they're not on EduBorg yet, send them a join
              code from your course instead.
            </span>
          </label>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card/50 px-5 py-3">
          {errorMessage && <span className="mr-auto text-xs text-destructive">{errorMessage}</span>}
          <button
            type="submit"
            disabled={!email.trim() || startMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {startMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Start DM
          </button>
        </footer>
      </form>
    </div>
  );
}
